/**
 * Simplified network status service for tracking online/offline status
 */
interface NetworkStatus {
  isOnline: boolean;
  connectionQuality?: 'good' | 'fair' | 'poor';
  lastChecked?: number;
}

// Cache key for storing network status
const NETWORK_STATUS_CACHE_KEY = 'networkStatus';

/**
 * Gets the current network status
 */
export const getNetworkStatus = (): NetworkStatus => {
  const status = localStorage.getItem(NETWORK_STATUS_CACHE_KEY);
  if (status) {
    try {
      return JSON.parse(status);
    } catch (e) {
      // Invalid status in cache
    }
  }
  return { isOnline: navigator.onLine };
};

/**
 * Updates the stored network status
 */
export const updateNetworkStatus = (updates: Partial<NetworkStatus>): void => {
  const current = getNetworkStatus();
  const updated = { ...current, ...updates, lastChecked: Date.now() };
  localStorage.setItem(NETWORK_STATUS_CACHE_KEY, JSON.stringify(updated));
};

/**
 * Determines if the device is on a low-quality connection
 */
export const isLowQualityConnection = (): boolean => {
  if ('connection' in navigator && (navigator as any).connection) {
    const conn = (navigator as any).connection;
    return conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
  }
  return false;
};

/**
 * Sets up event listeners for online/offline events
 */
export const setupNetworkListeners = (
  onOnline?: () => void,
  onOffline?: () => void
): () => void => {
  const handleOnline = () => {
    updateNetworkStatus({ isOnline: true });
    if (onOnline) onOnline();
  };

  const handleOffline = () => {
    updateNetworkStatus({ isOnline: false });
    if (onOffline) onOffline();
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

/**
 * Checks if the Supabase API is accessible
 */
export const checkApiConnection = async (): Promise<boolean> => {
  try {
    console.log('Checking Supabase connection...');
    
    // Create an AbortController to timeout the request if it takes too long
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    // Try to fetch from Supabase (using a lightweight call)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Supabase environment variables not found');
      return false;
    }
    
    const response = await fetch(`${supabaseUrl}/rest/v1/articles?select=count`, {
      method: 'HEAD', // Just get headers, no need for body
      headers: {
        'apikey': supabaseAnonKey,
        'Prefer': 'count=exact'
      },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    const isConnected = response.ok;
    updateNetworkStatus({ 
      connectionQuality: isConnected ? 'good' : 'poor',
      lastChecked: Date.now()
    });
    
    console.log('Supabase connection check result:', isConnected ? 'Connected' : 'Failed');
    return isConnected;
  } catch (err) {
    console.error('Supabase connection test failed:', err);
    updateNetworkStatus({ 
      connectionQuality: 'poor',
      lastChecked: Date.now()
    });
    return false;
  }
};